import { useState } from "react"
import { toast } from "sonner"
import type { TimelineBlockContent, TimelineItem } from "../types"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { ChevronUp, ChevronDown, Loader2, Upload, X } from "lucide-react"
import { supabase } from "@/lib/supabase"
import { uploadVersionedOrgImage } from "@/lib/uploadOrgImage"
import { useOrgStore } from "@/stores/orgStore"

const IMAGE_ACCEPT = "image/png,image/jpeg,image/svg+xml"
const IMAGE_MAX_BYTES = 2 * 1024 * 1024

interface TimelineBlockEditorProps {
  content: TimelineBlockContent | null
  onChange: (content: TimelineBlockContent) => void
  block?: { id: string; section_type: string }
}

export function TimelineBlockEditor({
  content,
  onChange,
  block,
}: TimelineBlockEditorProps) {
  const { currentOrg } = useOrgStore()
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null)

  const c = content ?? {}
  const items = (c.items ?? []) as TimelineItem[]
  const layout = (c.layout ?? "vertical") as "vertical" | "horizontal"
  const update = (partial: Partial<TimelineBlockContent>) =>
    onChange({ ...c, ...partial })

  const setItem = (i: number, field: keyof TimelineItem, val: string | undefined) => {
    const next = [...items]
    if (!next[i]) next[i] = {}
    next[i] = { ...next[i], [field]: val }
    update({ items: next })
  }

  const addItem = () => update({ items: [...items, {}] })
  const removeItem = (i: number) => update({ items: items.filter((_, j) => j !== i) })

  const moveItem = (from: number, direction: "up" | "down") => {
    const to = direction === "up" ? from - 1 : from + 1
    if (to < 0 || to >= items.length) return
    const next = [...items]
    ;[next[from], next[to]] = [next[to], next[from]]
    update({ items: next })
  }

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    itemIndex: number
  ) => {
    const file = e.target.files?.[0]
    if (!file || !currentOrg?.id) {
      if (!currentOrg?.id) toast.error("Organization not loaded")
      return
    }
    if (file.size > IMAGE_MAX_BYTES) {
      toast.error("Image must be 2MB or smaller")
      return
    }
    const allowed = IMAGE_ACCEPT.split(",").map((m) => m.trim())
    if (!allowed.some((m) => file.type === m)) {
      toast.error("Image must be PNG, JPEG, or SVG")
      return
    }
    setUploadingIndex(itemIndex)
    const blockId = block?.id ?? "temp"
    const { data: uploaded, error } = await uploadVersionedOrgImage(
      supabase,
      "logos",
      `${currentOrg.id}/timeline/${blockId}/${itemIndex}`,
      file
    )
    if (error || !uploaded) {
      setUploadingIndex(null)
      toast.error(error?.message ?? "Upload failed")
      return
    }
    setItem(itemIndex, "image_url", uploaded.publicUrl)
    setUploadingIndex(null)
    toast.success("Image uploaded")
    e.target.value = ""
  }

  const handleRemoveImage = (itemIndex: number) => {
    setItem(itemIndex, "image_url", undefined)
  }

  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-medium">Timeline</h3>
        <p className="text-sm text-muted-foreground">Chronological milestones.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="timeline-heading">Heading</Label>
          <Input
            id="timeline-heading"
            value={c.heading ?? "Our story"}
            onChange={(e) => update({ heading: e.target.value })}
            placeholder="Our story"
          />
        </div>

        <div className="flex items-center gap-2">
          <Label>Layout</Label>
          <div className="flex rounded-md border p-0.5">
            <Button
              type="button"
              variant={layout === "vertical" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-3"
              onClick={() => update({ layout: "vertical" })}
            >
              Vertical
            </Button>
            <Button
              type="button"
              variant={layout === "horizontal" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-3"
              onClick={() => update({ layout: "horizontal" })}
            >
              Horizontal
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <Label>Items</Label>
          {items.map((item, i) => (
            <div
              key={i}
              className="rounded-md border p-3 space-y-2"
              data-testid={`timeline-item-editor-${i}`}
            >
              <div className="flex gap-3">
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  {item.image_url ? (
                    <div className="relative group">
                      <img
                        src={item.image_url}
                        alt=""
                        className="h-16 w-24 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(i)}
                        className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Remove image"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div
                      className="h-16 w-24 rounded border bg-muted flex items-center justify-center text-muted-foreground text-xs"
                      title="Upload image"
                    >
                      Image
                    </div>
                  )}
                  <label className="flex justify-center cursor-pointer">
                    <input
                      type="file"
                      accept={IMAGE_ACCEPT}
                      className="sr-only"
                      onChange={(e) => handleImageUpload(e, i)}
                      disabled={uploadingIndex !== null}
                    />
                    {uploadingIndex === i ? (
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    ) : (
                      <Upload className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                    )}
                  </label>
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <Input
                    placeholder="Year"
                    value={item.year ?? ""}
                    onChange={(e) => setItem(i, "year", e.target.value)}
                  />
                  <Input
                    placeholder="Title"
                    value={item.title ?? ""}
                    onChange={(e) => setItem(i, "title", e.target.value)}
                  />
                  <Textarea
                    placeholder="Description"
                    value={item.description ?? ""}
                    onChange={(e) => setItem(i, "description", e.target.value)}
                    rows={2}
                    className="resize-none"
                  />
                </div>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moveItem(i, "up")}
                  disabled={i === 0}
                  aria-label="Move up"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moveItem(i, "down")}
                  disabled={i === items.length - 1}
                  aria-label="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeItem(i)}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addItem}>
            Add item
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
